import React from "react";
import { useTranslation } from "@lib/i18n";

import Typography from "@components/shared/Typography";
import DownloadIcon from "@components/shared/Icons/Download";
import { Button } from "@components/shared/Button";
import CollapsableContainer from "@components/shared/CollapsableContainer";

import gtm from "@gtm/core";
import { getStaticAssetURL } from "@lib/getStaticAssetURL";
import { openDocument } from "@lib/openDocument";
import { useGetAssetPath } from "hooks/useGetAssetPath";
import { PDF_DOCUMENT_TYPES } from "@shared/constants";
import { useActiveCarPreconfigurationSelector } from "@redux/reducers/deal";

import {
  displayTechnicalDetailsEvent,
  openCustomizeSectionEvent,
} from "@gtm/events/homepage";
import Icon from "@components/shared/Icons/Icon";

const TechnicalDetails = () => {
  const { t, i18n } = useTranslation();
  const getAssetPath = useGetAssetPath(i18n.language);

  const activePreconfiguration = useActiveCarPreconfigurationSelector();

  const openTechnicalDetails = () => {
    gtm.fire(displayTechnicalDetailsEvent());
    openDocument(getAssetPath(PDF_DOCUMENT_TYPES.TECHNICAL_CHARACTERISTICS));
  };

  const gtmEvent = prevState => {
    if (!prevState) {
      gtm.fire(openCustomizeSectionEvent(t("technicalDetails.title")));
    }
  };

  return (
    <CollapsableContainer
      data-id="technical-details-open"
      title={t("technicalDetails.title")}
      onChange={gtmEvent}
    >
      <div className="flex flex-col items-center gap-4 w-full">
        {t("technicalDetails.importantInformation") && (
          <Button
            className="mb-4"
            onClick={() =>
              openDocument(
                getAssetPath(PDF_DOCUMENT_TYPES.IMPORTANT_INFORMATION)
              )
            }
            size="small"
            variant="secondary"
          >
            <Icon
              className="size-6 mr-2"
              icon={<DownloadIcon data-id="technical-details-icon-download" />}
            />
            {t("technicalDetails.importantInformation")}
          </Button>
        )}
        <div
          className="flex justify-center w-full mb-8"
          data-id="technical-details-images"
        >
          <div className="flex flex-col items-start min-w-0">
            <img
              alt={t("technicalDetails.front")}
              className="h-20 mb-4 mix-blend-multiply object-contain lg:h-26"
              src={getStaticAssetURL(
                "/images/gallery/technical-characteristics/model_front.jpg"
              )}
              title={t("technicalDetails.front")}
            />
            <div className="w-full h-0.5 bg-greyLight opacity-50 mb-1" />
            <Typography className="uppercase font-semibold">
              {t("technicalDetails.width")}
            </Typography>
            <Typography>
              {activePreconfiguration.width}
              {t("general.meterSymbol")}
            </Typography>
          </div>
          <div className="flex flex-col items-start min-w-0">
            <div className="flex items-start h-32 mx-2">
              <div className="w-0.5 h-full bg-greyLight opacity-50 ml-1" />
              <div className="flex flex-col pl-1">
                <Typography className="uppercase font-semibold">
                  {t("technicalDetails.height")}
                </Typography>
                <Typography>
                  {activePreconfiguration.height}
                  {t("general.meterSymbol")}
                </Typography>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-start min-w-0">
            <img
              alt={t("technicalDetails.side")}
              className="h-20 mb-4 lg:h-26 object-contain mix-blend-multiply"
              src={getStaticAssetURL(
                "/images/gallery/technical-characteristics/model_side.jpg"
              )}
              title={t("technicalDetails.side")}
            />
            <div className="w-full h-0.5 bg-greyLight opacity-50 mb-1" />
            <Typography className="uppercase font-semibold">
              {t("technicalDetails.length")}
            </Typography>
            <Typography>
              {activePreconfiguration.length}
              {t("general.meterSymbol")}
            </Typography>
          </div>
        </div>
        <div
          className="flex flex-col gap-4 w-full"
          data-id="technical-details-info"
        >
          {activePreconfiguration.technicalCharacteristics.map(
            ({ label, description }, key) => (
              <div className="flex justify-between w-full" key={key}>
                <Typography className="text-sm uppercase font-semibold">
                  {label}
                </Typography>
                <Typography className="text-sm">{description}</Typography>
              </div>
            )
          )}
        </div>
        <div
          className="flex flex-col gap-2 w-full"
          data-id="technical-details-info"
        >
          <Typography className="text-sm uppercase font-semibold mt-8">
            {t("technicalDetails.descriptionTitle")}
          </Typography>
          <Typography className="text-sm/normal">
            {t("technicalDetails.description")}
          </Typography>
        </div>
        <img
          alt={t("general.technicalCharacteristicsLabel", {
            preconfigurationLabel: activePreconfiguration.label,
          })}
          className="w-full rounded-md"
          src={getStaticAssetURL(
            "/images/gallery/technical-characteristics/additionnal.jpg"
          )}
          title={t("general.technicalCharacteristicsLabel", {
            preconfigurationLabel: activePreconfiguration.label,
          })}
        />
        <Button
          className="mt-4"
          onClick={openTechnicalDetails}
          variant="secondary"
        >
          <Icon
            className="size-6 mr-2"
            icon={<DownloadIcon data-id="technical-details-icon-download" />}
          />
          {t("technicalDetails.techList")}
        </Button>
      </div>
    </CollapsableContainer>
  );
};

export default TechnicalDetails;
